import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Printer, X } from "lucide-react";

const TIPOS = [
  { v: "FACTURA", label: "Factura", serie_default: "F001" },
  { v: "BOLETA", label: "Boleta", serie_default: "B001" },
  { v: "NOTA_CREDITO", label: "Nota de Crédito", serie_default: "FC01" },
  { v: "NOTA_DEBITO", label: "Nota de Débito", serie_default: "FD01" },
];

const fmt = (n) => Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Ventas() {
  const { empresa, formatApiError } = useApp();
  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [open, setOpen] = useState(false);
  const [printDoc, setPrintDoc] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState("ALL");
  const [filtroDesde, setFiltroDesde] = useState("");
  const [filtroHasta, setFiltroHasta] = useState("");

  const blankItem = { producto_id: "", codigo: "", descripcion: "", unidad: "NIU", cantidad: 1, precio_unitario: 0, descuento: 0, afecto_igv: true };
  const blankForm = {
    tipo: "FACTURA", serie: "F001", numero: 1,
    fecha_emision: new Date().toISOString().slice(0, 10),
    cliente_id: "", moneda: "PEN", tipo_cambio: 1, forma_pago: "CONTADO",
    items: [{ ...blankItem }], observaciones: "",
  };
  const [form, setForm] = useState(blankForm);

  const load = async () => {
    const params = new URLSearchParams();
    if (filtroTipo !== "ALL") params.set("tipo", filtroTipo);
    if (filtroDesde) params.set("desde", filtroDesde);
    if (filtroHasta) params.set("hasta", filtroHasta);
    const { data } = await api.get(`/ventas?${params}`);
    setItems(data);
  };
  useEffect(() => { if (empresa) { load(); api.get("/clientes").then((r) => setClientes(r.data)); api.get("/productos").then((r) => setProductos(r.data)); } /* eslint-disable-next-line */ }, [empresa, filtroTipo, filtroDesde, filtroHasta]);

  const setTipo = async (tipo) => {
    const t = TIPOS.find((x) => x.v === tipo);
    const serie = t.serie_default;
    setForm((f) => ({ ...f, tipo, serie }));
    try {
      const { data } = await api.get(`/ventas/siguiente-numero?tipo=${tipo}&serie=${serie}`);
      setForm((f) => ({ ...f, tipo, serie, numero: data.siguiente }));
    } catch {}
  };

  const totales = useMemo(() => {
    let sg = 0, si = 0;
    form.items.forEach((it) => {
      const neto = (+it.cantidad || 0) * (+it.precio_unitario || 0) - (+it.descuento || 0);
      if (it.afecto_igv) sg += neto; else si += neto;
    });
    const igv = sg * 0.18;
    return { sg, si, igv, total: sg + si + igv };
  }, [form.items]);

  const setItem = (idx, field, value) => {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...f, items };
    });
  };
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...blankItem }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const pickProducto = (idx, pid) => {
    const p = productos.find((x) => x.id === pid);
    if (!p) return;
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], producto_id: p.id, codigo: p.codigo, descripcion: p.descripcion, unidad: p.unidad, precio_unitario: p.precio_venta, afecto_igv: p.afecto_igv };
      return { ...f, items };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.cliente_id) { toast.error("Selecciona un cliente"); return; }
    if (form.items.length === 0) { toast.error("Agrega al menos un item"); return; }
    try {
      const payload = {
        ...form,
        numero: +form.numero,
        items: form.items.map((it) => ({ ...it, cantidad: +it.cantidad, precio_unitario: +it.precio_unitario, descuento: +it.descuento })),
      };
      const { data } = await api.post("/ventas", payload);
      toast.success(`${data.tipo} ${data.serie}-${data.numero} emitido`);
      setOpen(false); setForm(blankForm); load(); setPrintDoc(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const anular = async (v) => {
    if (!confirm(`¿Anular ${v.tipo} ${v.serie}-${v.numero}?`)) return;
    await api.delete(`/ventas/${v.id}`); toast.success("Anulado"); load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Ventas</h1>
          <p className="text-[13px] text-slate-500">Emisión de Facturas, Boletas y Notas de Crédito/Débito.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setTipo("FACTURA"); else setForm(blankForm); }}>
          <DialogTrigger asChild>
            <Button data-testid="btn-nueva-venta" className="h-9 bg-slate-900 hover:bg-slate-800 text-white">
              <Plus className="w-4 h-4 mr-1" /> Nuevo comprobante
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Emisión de comprobante</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4 text-sm">
              <div className="grid grid-cols-6 gap-3">
                <div className="col-span-2">
                  <Label className="text-[12px]">Tipo</Label>
                  <Select value={form.tipo} onValueChange={setTipo}>
                    <SelectTrigger data-testid="venta-tipo" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIPOS.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[12px]">Serie</Label>
                  <Input value={form.serie} onChange={(e) => setForm({ ...form, serie: e.target.value })} className="h-9 mono" />
                </div>
                <div>
                  <Label className="text-[12px]">Número</Label>
                  <Input type="number" value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} className="h-9 mono" />
                </div>
                <div>
                  <Label className="text-[12px]">Fecha emisión</Label>
                  <Input type="date" value={form.fecha_emision} onChange={(e) => setForm({ ...form, fecha_emision: e.target.value })} className="h-9" />
                </div>
                <div>
                  <Label className="text-[12px]">Moneda</Label>
                  <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="PEN">PEN (Soles)</SelectItem><SelectItem value="USD">USD (Dólares)</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="col-span-4">
                  <Label className="text-[12px]">Cliente</Label>
                  <Select value={form.cliente_id} onValueChange={(v) => setForm({ ...form, cliente_id: v })}>
                    <SelectTrigger data-testid="venta-cliente" className="h-9"><SelectValue placeholder="Selecciona cliente" /></SelectTrigger>
                    <SelectContent>
                      {clientes.map((c) => <SelectItem key={c.id} value={c.id}><span className="mono mr-2">{c.numero_doc}</span>{c.razon_social}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[12px]">Forma de pago</Label>
                  <Select value={form.forma_pago} onValueChange={(v) => setForm({ ...form, forma_pago: v })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="CONTADO">Contado</SelectItem><SelectItem value="CREDITO">Crédito</SelectItem></SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[12px]">T.C.</Label>
                  <Input type="number" step="0.001" value={form.tipo_cambio} onChange={(e) => setForm({ ...form, tipo_cambio: e.target.value })} className="h-9 tabular text-right" />
                </div>
              </div>

              <div className="border border-slate-200 rounded">
                <table className="tbl w-full">
                  <thead><tr><th>Producto</th><th>Descripción</th><th>UM</th><th className="text-right">Cant.</th><th className="text-right">P. Unit</th><th className="text-right">Dscto</th><th>IGV</th><th className="text-right">Subtotal</th><th></th></tr></thead>
                  <tbody>
                    {form.items.map((it, idx) => (
                      <tr key={idx}>
                        <td className="w-48">
                          <Select value={it.producto_id || "none"} onValueChange={(v) => v !== "none" && pickProducto(idx, v)}>
                            <SelectTrigger className="h-8 text-[12px]"><SelectValue placeholder="Producto" /></SelectTrigger>
                            <SelectContent>{productos.map((p) => <SelectItem key={p.id} value={p.id}>{p.codigo} - {p.descripcion}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td><Input value={it.descripcion} onChange={(e) => setItem(idx, "descripcion", e.target.value)} className="h-8 text-[12px]" /></td>
                        <td className="w-16"><Input value={it.unidad} onChange={(e) => setItem(idx, "unidad", e.target.value)} className="h-8 text-[12px] mono" /></td>
                        <td className="w-20"><Input type="number" step="0.01" value={it.cantidad} onChange={(e) => setItem(idx, "cantidad", e.target.value)} className="h-8 text-[12px] tabular text-right" /></td>
                        <td className="w-24"><Input type="number" step="0.01" value={it.precio_unitario} onChange={(e) => setItem(idx, "precio_unitario", e.target.value)} className="h-8 text-[12px] tabular text-right" /></td>
                        <td className="w-20"><Input type="number" step="0.01" value={it.descuento} onChange={(e) => setItem(idx, "descuento", e.target.value)} className="h-8 text-[12px] tabular text-right" /></td>
                        <td className="text-center"><input type="checkbox" checked={it.afecto_igv} onChange={(e) => setItem(idx, "afecto_igv", e.target.checked)} /></td>
                        <td className="tabular text-right font-medium">{fmt((it.cantidad * it.precio_unitario) - (+it.descuento || 0))}</td>
                        <td><button type="button" onClick={() => removeItem(idx)} className="text-rose-500 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="p-2 border-t border-slate-200">
                  <Button type="button" onClick={addItem} variant="outline" size="sm" data-testid="add-item" className="h-7 text-[12px]"><Plus className="w-3 h-3 mr-1" /> Agregar línea</Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[12px]">Observaciones</Label>
                  <Input value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} className="h-9" />
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded p-3 text-[12px] tabular">
                  <div className="flex justify-between py-0.5"><span className="text-slate-600">Subtotal gravado</span><span className="font-medium">S/ {fmt(totales.sg)}</span></div>
                  <div className="flex justify-between py-0.5"><span className="text-slate-600">Inafecto</span><span>S/ {fmt(totales.si)}</span></div>
                  <div className="flex justify-between py-0.5"><span className="text-slate-600">IGV (18%)</span><span className="font-medium">S/ {fmt(totales.igv)}</span></div>
                  <div className="flex justify-between pt-2 mt-1 border-t border-slate-300 text-sm"><span className="font-bold text-slate-800">TOTAL</span><span className="font-bold text-slate-900">S/ {fmt(totales.total)}</span></div>
                </div>
              </div>

              <DialogFooter><Button data-testid="venta-submit" type="submit" className="bg-slate-900 text-white">Emitir comprobante</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="p-3 border-b border-slate-200 flex flex-wrap gap-3">
          <Select value={filtroTipo} onValueChange={setFiltroTipo}>
            <SelectTrigger className="h-9 w-44 text-[12px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos los tipos</SelectItem>
              {TIPOS.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={filtroDesde} onChange={(e) => setFiltroDesde(e.target.value)} className="h-9 w-40 text-[12px]" placeholder="Desde" />
          <Input type="date" value={filtroHasta} onChange={(e) => setFiltroHasta(e.target.value)} className="h-9 w-40 text-[12px]" placeholder="Hasta" />
        </div>
        <table className="tbl w-full">
          <thead><tr><th>Fecha</th><th>Tipo</th><th>Comprobante</th><th>Cliente</th><th className="text-right">Sub. Grav.</th><th className="text-right">IGV</th><th className="text-right">Total</th><th>Estado</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 ? <tr><td colSpan={9} className="text-center py-6 text-slate-500">Sin comprobantes en el periodo</td></tr>
              : items.map((v) => (
                <tr key={v.id}>
                  <td className="mono">{v.fecha_emision?.slice(0, 10)}</td>
                  <td><span className="badge badge-info">{v.tipo.replace("_", " ")}</span></td>
                  <td className="mono font-medium">{v.serie}-{String(v.numero).padStart(8, "0")}</td>
                  <td>
                    <div className="font-medium text-slate-800">{v.cliente?.razon_social}</div>
                    <div className="text-[10px] mono text-slate-500">{v.cliente?.numero_doc}</div>
                  </td>
                  <td className="tabular text-right">{fmt(v.subtotal_gravado)}</td>
                  <td className="tabular text-right">{fmt(v.igv)}</td>
                  <td className="tabular text-right font-bold">{fmt(v.total)}</td>
                  <td>
                    {v.estado_sunat === "ANULADO" ? <span className="badge badge-error">Anulado</span> : <span className="badge badge-success">Emitido</span>}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={() => setPrintDoc(v)} className="text-slate-600 hover:text-blue-600 mr-2" data-testid={`print-${v.id}`}><Printer className="w-4 h-4" /></button>
                    {v.estado_sunat !== "ANULADO" && (
                      <button onClick={() => anular(v)} className="text-slate-600 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {printDoc && <PrintModal doc={printDoc} empresa={empresa} onClose={() => setPrintDoc(null)} />}
    </div>
  );
}

function PrintModal({ doc, empresa, onClose }) {
  const totalLetras = numeroALetras(doc.total) + ` ${doc.moneda === "PEN" ? "SOLES" : "DÓLARES"}`;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white max-w-3xl w-full rounded-md shadow-lg">
        <div className="flex items-center justify-between p-3 border-b border-slate-200 print:hidden">
          <div className="text-sm font-bold">Vista previa del comprobante</div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => window.print()}><Printer className="w-3.5 h-3.5 mr-1" />Imprimir</Button>
            <Button size="sm" variant="outline" onClick={onClose}><X className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
        <div className="p-8 text-[12px] text-slate-800">
          <div className="grid grid-cols-3 gap-4 pb-4 border-b-2 border-slate-900">
            <div className="col-span-2">
              <h2 className="text-xl font-bold tracking-tight">{empresa?.razon_social}</h2>
              <div className="text-[11px] text-slate-600 mt-1">
                RUC: <span className="mono font-medium">{empresa?.ruc}</span><br />
                {empresa?.direccion}<br />
                {empresa?.telefono} {empresa?.email && `• ${empresa.email}`}
              </div>
            </div>
            <div className="border-2 border-slate-900 rounded text-center p-3">
              <div className="text-[10px] uppercase tracking-wider">{doc.tipo.replace("_", " ")} ELECTRÓNICA</div>
              <div className="mono font-bold text-base mt-1">{doc.serie}-{String(doc.numero).padStart(8, "0")}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 py-3 text-[11px]">
            <div>
              <div className="text-slate-500 uppercase text-[10px] tracking-wider">Cliente</div>
              <div className="font-bold">{doc.cliente?.razon_social}</div>
              <div>{doc.cliente?.tipo_doc}: <span className="mono">{doc.cliente?.numero_doc}</span></div>
            </div>
            <div className="text-right">
              <div><span className="text-slate-500">Fecha emisión:</span> <span className="font-medium mono">{doc.fecha_emision?.slice(0, 10)}</span></div>
              <div><span className="text-slate-500">Moneda:</span> <span className="font-medium mono">{doc.moneda}</span></div>
              <div><span className="text-slate-500">Forma de pago:</span> <span className="font-medium">{doc.forma_pago}</span></div>
            </div>
          </div>

          <table className="w-full text-[11px] mt-2">
            <thead><tr className="border-y border-slate-300 bg-slate-50">
              <th className="text-left py-2 px-2 uppercase tracking-wider text-[10px]">Cant.</th>
              <th className="text-left py-2 px-2 uppercase tracking-wider text-[10px]">UM</th>
              <th className="text-left py-2 px-2 uppercase tracking-wider text-[10px]">Descripción</th>
              <th className="text-right py-2 px-2 uppercase tracking-wider text-[10px]">P. Unit</th>
              <th className="text-right py-2 px-2 uppercase tracking-wider text-[10px]">Total</th>
            </tr></thead>
            <tbody>
              {doc.items?.map((it, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-1.5 px-2 mono">{it.cantidad}</td>
                  <td className="py-1.5 px-2 mono">{it.unidad}</td>
                  <td className="py-1.5 px-2">{it.descripcion}</td>
                  <td className="py-1.5 px-2 tabular text-right">{fmt(it.precio_unitario)}</td>
                  <td className="py-1.5 px-2 tabular text-right font-medium">{fmt(it.cantidad * it.precio_unitario - (it.descuento || 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="grid grid-cols-2 gap-6 mt-4 text-[11px]">
            <div>
              <div className="text-slate-500 uppercase text-[10px] tracking-wider mb-1">Son:</div>
              <div className="border border-slate-300 p-2 italic uppercase">{totalLetras}</div>
              {doc.observaciones && <div className="mt-2 text-slate-600">Obs: {doc.observaciones}</div>}
            </div>
            <div className="tabular">
              <div className="flex justify-between py-1"><span className="text-slate-600">Op. Gravada</span><span className="font-medium mono">{doc.moneda} {fmt(doc.subtotal_gravado)}</span></div>
              <div className="flex justify-between py-1"><span className="text-slate-600">Op. Inafecta</span><span className="mono">{doc.moneda} {fmt(doc.subtotal_inafecto)}</span></div>
              <div className="flex justify-between py-1"><span className="text-slate-600">IGV (18%)</span><span className="font-medium mono">{doc.moneda} {fmt(doc.igv)}</span></div>
              <div className="flex justify-between py-2 border-t border-slate-900 mt-2 text-sm"><span className="font-bold">TOTAL</span><span className="font-bold mono">{doc.moneda} {fmt(doc.total)}</span></div>
            </div>
          </div>

          <div className="mt-8 pt-4 border-t border-slate-200 text-center text-[10px] text-slate-500">
            Representación impresa del comprobante electrónico. Consulte su validez en www.sunat.gob.pe
          </div>
        </div>
      </div>
    </div>
  );
}

function numeroALetras(n) {
  const num = Math.floor(n);
  const dec = Math.round((n - num) * 100);
  return `${num.toLocaleString("es-PE")} CON ${String(dec).padStart(2, "0")}/100`;
}
